/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { GuideProgram } from 'client-code/filtered-guide';

import { Channel, GuideCell } from '../../../../core/entities';
import { GuideType, InteractiveComponents } from '../../../../core/enums';
import { FilteredGuideInteractor, ServicesClientImpl } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { ScreenTaskControllerTypes } from '../../../../core/navigation';
import { NetworkManager } from '../../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../../core/playback/impls';
import { TChannelV2 } from '../../../../core/types';
import { isJumpChannelCcbSwitchEnabled } from '../../../../core/utils/ccb/CcbSwitchHelper';
import { IPlayable } from '../../../../core/videoplayer/interfaces';
import { showChannelOptionsOverlay } from '../../../overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import { showEmptyRecordOverlay } from '../../../overlay/emptyrecordoverlay/emptyrecordoverlay.reducers';
import {
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    showPCPinOverlayWithProgramRating,
} from '../../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { showRecordOverlay } from '../../../overlay/recordoverlay/recordoverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    onKeyUpOverlayDialog,
} from '../../../overlaydialog/overlaydialog.reducers';
import {
    startVideoPlayback,
    triggerPlaybackOnPinVerify,
} from '../../../videoplayer/videoplayer.reducers';
import { showWhisper } from '../../../whisper/whisper.reducer';
import {
    cancelGuideSpinnerOperations,
    onKeyLeftGuideOperations,
    onKeyRightGuideOperations,
} from '../../guidenavigation/guidekeyoperations.reducers';
import {
    onRepaintFilteredGuideAction,
    onSetFilteredGuideTurboScrollAction,
} from '../filteredguide.actions';
import {
    filteredGuideGetPreviewAndMetadata,
    filteredGuideWatchNowLiveProgram,
    updateRecordingSharedPreferenceBeforeOpeningOverlay,
} from '../filteredguide.reducers';
import { onHorizontalKeyPressFilteredGuideAction } from './filteredguide.navigation.actions';

export const eventDispatcher = (keyCode: string, start = true, end = true) => {
    if (!keyCode || (!start && !end)) return;

    const eventInit = {
        key: keyCode,
    };

    if (start) window.dispatchEvent(new KeyboardEvent('keydown', eventInit));
    if (end) window.dispatchEvent(new KeyboardEvent('keyup', eventInit));
};

export function getHighlightPosition(
    value: string | null | undefined,
): { horizontal: number; vertical: number } | null {
    if (!value) return null;

    const [horizontalStr, verticalStr] = value.split('_');
    if (!horizontalStr || !verticalStr) return null;

    const horizontal = Number(horizontalStr);
    const vertical = Number(verticalStr);

    if (Number.isNaN(horizontal) || Number.isNaN(vertical)) return null;

    return { horizontal, vertical };
}

export const onKeyOkDefaultTaskFilteredGuide =
    (isLongPress = false, showMetadataInRecordingOverlay = false) =>
    async (dispatch: Function, getState: Function) => {
        const { selectedProgram, horizontalPosition } = getState().filteredGuide;

        const { currentOfferPreview, isExplicitlyAdult, isContentPCViolated, channel } =
            await filteredGuideGetPreviewAndMetadata(
                getState().filteredGuide,
                getState().channelOptionsOverlay,
            );

        if (!channel) {
            return;
        }

        if (horizontalPosition === 0) {
            dispatch(showChannelOptionsOverlay(channel, GuideType.FILTERED_GUIDE));
            return;
        }

        if (!currentOfferPreview) {
            const services = new ServicesClientImpl();

            const invokeEmptyRecordOverlay = () => async () => {
                const { movieYear } = selectedProgram;
                const movieYearText = movieYear ? ` (${movieYear})` : '';
                const title = `${selectedProgram.title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)}${movieYearText}`;

                await dispatch(
                    showEmptyRecordOverlay({
                        channel,
                        guideCell: new GuideCell(selectedProgram, channel.channelLogoUrl),
                        isContentAdult: selectedProgram.isAdult,
                        contentTitle: title,
                    }),
                );
            };
            if (selectedProgram.isAdult && services.shouldHideMetadata()) {
                dispatch(showPCPinOverlayWithProgramRating(invokeEmptyRecordOverlay, true));
            } else dispatch(invokeEmptyRecordOverlay());
            return;
        }

        if (currentOfferPreview.contentPreview?.isLive && !isLongPress) {
            dispatch(filteredGuideWatchNowLiveProgram(channel, isExplicitlyAdult, false));
        } else if (!currentOfferPreview.contentPreview?.isLive || isLongPress) {
            // handle the case when the user wants to open the record overlay
            const invokeRecordOverlay = () => async () => {
                await dispatch(
                    showRecordOverlay({
                        channel,
                        guideCell: new GuideCell(
                            {
                                ...selectedProgram,
                                duration: selectedProgram?.endTime - selectedProgram?.startTime,
                            },
                            channel.channelLogoUrl,
                        ),
                        offerPreview: currentOfferPreview,
                        showMetaData: showMetadataInRecordingOverlay,
                    }),
                );
                if (isExplicitlyAdult) dispatch(triggerPlaybackOnPinVerify());
            };
            updateRecordingSharedPreferenceBeforeOpeningOverlay(selectedProgram);
            if (isExplicitlyAdult && isContentPCViolated) {
                dispatch(showPCPinOverlayWithProgramRating(invokeRecordOverlay, isExplicitlyAdult));
            } else dispatch(invokeRecordOverlay());
        }
    };

// Usage:
// if (handleGuideErrorOrNetwork(dispatch, isError)) return;
export const handleGuideErrorOrNetwork = (dispatch: Function, isError: boolean) => {
    if (isError) {
        dispatch(onRepaintFilteredGuideAction());
        return true;
    }
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showWhisper());
        return true;
    }
    return false;
};

export function dispatchOverlayVerticalKey(
    dispatch: Function,
    screenTaskController: string,
    direction: 'up' | 'down',
) {
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            return direction === 'up'
                ? dispatch(onKeyUpPcPinOverlay())
                : dispatch(onKeyDownPcPinOverlay());
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            return direction === 'up'
                ? dispatch(onKeyUpOverlayDialog())
                : dispatch(onKeyDownOverlayDialog());
        default:
    }
}

export const handleLongKeyNavigation = (
    dispatch: Function,
    screenTaskController: string,
    direction: InteractiveComponents.NORTH | InteractiveComponents.SOUTH,
) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showWhisper());
        return;
    }
    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS) {
        return;
    }
    dispatch(onSetFilteredGuideTurboScrollAction(true, direction));
};

export function dispatchOverlayHorizontalKey(
    dispatch: Function,
    screenTaskController: string,
    direction: 'left' | 'right',
) {
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            return direction === 'left'
                ? dispatch(onKeyLeftOverlayDialog())
                : dispatch(onKeyRightOverlayDialog());
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            return direction === 'left'
                ? dispatch(onKeyLeftPcPinOverlay())
                : dispatch(onKeyRightPcPinOverlay());
        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            return direction === 'left'
                ? dispatch(onKeyLeftGuideOperations())
                : dispatch(onKeyRightGuideOperations());
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            return direction === 'left'
                ? dispatch(onKeyLeftOverlayDialog())
                : dispatch(onKeyRightOverlayDialog());
        default:
    }
}

export const handleHorizontalKeyNavigation = (
    dispatch: Function,
    isError: boolean,
    screenTaskController: string,
    direction: 'left' | 'right',
) => {
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            if (handleGuideErrorOrNetwork(dispatch, isError)) return;
            dispatch(onHorizontalKeyPressFilteredGuideAction(direction === 'left' ? 0 : 1));
            break;
        default:
            dispatchOverlayHorizontalKey(dispatch, screenTaskController, direction);
    }
};

export const handleLongKeyChannelNavigation = (
    dispatch: Function,
    isError: boolean,
    screenTaskController: string,
    direction: 'up' | 'down',
) => {
    if (handleGuideErrorOrNetwork(dispatch, isError)) return;

    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS) {
        return;
    }

    // Inline the logic instead of calling the reducer function
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showWhisper());
        return;
    }

    const interactiveDirection =
        direction === 'up' ? InteractiveComponents.NORTH : InteractiveComponents.SOUTH;
    dispatch(onSetFilteredGuideTurboScrollAction(true, interactiveDirection));
};

export const onKeyOkVodResumeStartOverOverlayFromFilteredGuide =
    () => async (dispatch: Function, getState: Function) => {
        const { overlayHighlight, overlayDialog, filteredGuide } = getState();

        const { selectedProgram }: { selectedProgram: GuideProgram | null } = filteredGuide;
        const filteredGuideInteractor = new FilteredGuideInteractor();
        const fetchChannels = isJumpChannelCcbSwitchEnabled()
            ? filteredGuideInteractor.fetchChannelsV2
            : filteredGuideInteractor.fetchChannels;
        const channels = await fetchChannels('');
        const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
        const { options } = overlayDialog.uiProperties;

        const channel: Channel | TChannelV2 = (
            channels.channels as Array<Channel | TChannelV2>
        ).find((c: Channel | TChannelV2) => c.channelId === selectedProgram?.channelId)!;
        const playbackContentType: PlaybackContentType =
            options[overlayHighlightVerticalPosition].value;

        const playable: IPlayable = { channel, playbackContentType };

        PlaybackDataModelImpl.getInstance().setPlaybackData(playbackContentType);
        dispatch(hideOverlayDialog());
        dispatch(startVideoPlayback(playable));
    };
